import React, { useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Form } from 'antd';
import useAuthContext from '../../../hooks/useAuthContext';
import { fetchUserAttributes, signIn } from 'aws-amplify/auth';
import { useNotify } from '../../../providers/NotificationProvider';
import Auth from '../../../service/Auth';
import Logo from '../../../assets/img/LogoÑuñoa.png';
import { FiMail, FiLock, FiEye, FiEyeOff, FiArrowRight, FiLoader } from 'react-icons/fi';

/* ── Input field ── */
const Field = ({ label, error, children }) => (
  <div className="flex flex-col gap-1.5">
    <label className="text-[13px] font-bold text-gray-700">{label}</label>
    {children}
    {error && <p className="text-[12px] text-red-500 font-medium">{error}</p>}
  </div>
);

const LoginPage = () => {
  const { authIsSuccess } = useAuthContext();
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const [errors, setErrors] = useState({});
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const navigate = useNavigate();
  const notify = useNotify();

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    const errs = {};
    if (!email) errs.email = 'El email es requerido';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) errs.email = 'Formato de email inválido';
    if (!password) errs.password = 'La contraseña es requerida';
    else if (password.length < 6) errs.password = 'Mínimo 6 caracteres';
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setErrors({});
    setIsLoading(true);

    try {
      const result = await signIn({ username: email, password });

      // Defensive: nextStep puede venir undefined en edge cases de Amplify
      if (result?.nextStep?.signInStep === 'CONFIRM_SIGN_UP') {
        notify.warning('Debes confirmar tu cuenta primero');
        navigate('/confirmar-cuenta', { state: { email } });
        return;
      }

      // Validar que signIn fue exitoso antes de seguir
      if (!result?.isSignedIn && result?.nextStep?.signInStep !== 'DONE') {
        notify.warning('Proceso de login incompleto. Revisa los datos e intenta de nuevo.');
        return;
      }

      let attrs = {};
      try {
        attrs = await fetchUserAttributes();
      } catch (attrErr) {
        console.warn('No se pudieron obtener atributos, usando valores por defecto:', attrErr);
      }

      notify.success(`¡Bienvenido, ${attrs?.name || 'Usuario'}!`);

      try {
        authIsSuccess(attrs);
      } catch (authErr) {
        console.error('Error actualizando contexto de auth:', authErr);
      }

      setTimeout(() => {
        try {
          navigate('/');
          window.location.reload();
        } catch (navErr) {
          console.error('Error redirigiendo tras login:', navErr);
          window.location.href = '/';
        }
      }, 400);

    } catch (err) {
      if (err.name === 'UserNotConfirmedException') {
        notify.warning('Cuenta no confirmada. Revisa tu email.');
        navigate('/confirmar-cuenta', { state: { email } });
        return;
      }
      if (err.name === 'NotAuthorizedException') {
        try {
          const status = await Auth.checkUserStatus(email);
          if (status.userExists && status.requiresConfirmation) {
            notify.warning('Tu cuenta no está activada. Revisa tu email.');
            navigate('/confirmar-cuenta', { state: { email } });
            return;
          }
          notify.error('Email o contraseña incorrectos');
        } catch { notify.error('Email o contraseña incorrectos'); }
        return;
      }
      if (err.name === 'UserNotFoundException') {
        notify.error('No existe cuenta con este email');
        return;
      }
      notify.error(err.message || 'Error al iniciar sesión');
    } finally { setIsLoading(false); }
  }, [email, password, notify, authIsSuccess, navigate]);

  return (
    <div className="min-h-screen flex" style={{ background: '#f4f6f9' }}>

      {/* ── Panel izquierdo: formulario ── */}
      <div className="flex-1 flex flex-col justify-center px-6 py-10 sm:px-10 lg:px-16 max-w-lg w-full mx-auto lg:mx-0">

        {/* Logo */}
        <Link to="/" className="flex items-center gap-2 mb-10">
          <img src={Logo} alt="Ñuñoa Deportes" className="h-10 w-auto" />
        </Link>

        {/* Heading */}
        <div className="mb-8">
          <h1 className="text-3xl font-extrabold text-gray-900 mb-2">Bienvenido 👋</h1>
          <p className="text-gray-500 text-sm">Ingresa a tu cuenta para continuar</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-5" noValidate>
          <Field label="Correo electrónico" error={errors.email}>
            <div className={`flex items-center gap-2 bg-white rounded-2xl border px-4 py-3 transition-all
              ${errors.email ? 'border-red-300 ring-2 ring-red-100' : 'border-gray-200 focus-within:border-blue-400 focus-within:ring-2 focus-within:ring-blue-100'}`}>
              <FiMail size={17} className="text-gray-400 flex-shrink-0" />
              <input
                type="email" value={email} onChange={e => setEmail(e.target.value)}
                placeholder="tu@email.com" autoComplete="email"
                className="flex-1 bg-transparent text-sm text-gray-900 outline-none placeholder-gray-400"
                disabled={isLoading}
              />
            </div>
          </Field>

          <Field label="Contraseña" error={errors.password}>
            <div className={`flex items-center gap-2 bg-white rounded-2xl border px-4 py-3 transition-all
              ${errors.password ? 'border-red-300 ring-2 ring-red-100' : 'border-gray-200 focus-within:border-blue-400 focus-within:ring-2 focus-within:ring-blue-100'}`}>
              <FiLock size={17} className="text-gray-400 flex-shrink-0" />
              <input
                type={showPwd ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)}
                placeholder="••••••••" autoComplete="current-password"
                className="flex-1 bg-transparent text-sm text-gray-900 outline-none placeholder-gray-400"
                disabled={isLoading}
              />
              <button type="button" onClick={() => setShowPwd(!showPwd)} className="text-gray-400 hover:text-gray-600 flex-shrink-0">
                {showPwd ? <FiEyeOff size={17} /> : <FiEye size={17} />}
              </button>
            </div>
          </Field>

          <div className="flex justify-end">
            <Link to="/olvidar-contrasena" className="text-[13px] font-semibold text-blue-600 hover:text-blue-700 transition-colors">
              ¿Olvidaste tu contraseña?
            </Link>
          </div>

          <button type="submit" disabled={isLoading}
            className="w-full flex items-center justify-center gap-2 py-3.5 rounded-2xl font-bold text-white text-sm transition-all disabled:opacity-60 hover:brightness-105 active:scale-[.98] shadow-md"
            style={{ background: 'linear-gradient(135deg,#003a94,#0047bb)' }}>
            {isLoading
              ? <><span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Iniciando sesión…</>
              : <> Iniciar sesión <FiArrowRight size={16} /></>
            }
          </button>
        </form>

        {/* Registro */}
        <p className="mt-8 text-center text-sm text-gray-500">
          ¿No tienes cuenta?{' '}
          <Link to="/registro" className="font-bold text-blue-600 hover:text-blue-700 transition-colors">
            Regístrate gratis
          </Link>
        </p>

        {/* Legal */}
        <p className="mt-6 text-center text-[11px] text-gray-400">
          Al ingresar aceptas nuestros{' '}
          <Link to="/privacy" className="underline hover:text-gray-600">Términos y Privacidad</Link>
        </p>
      </div>

      {/* ── Panel derecho — solo desktop ── */}
      <div className="hidden lg:flex flex-1 relative overflow-hidden">
        {/* Imagen de fondo */}
        <img
          src="https://images.unsplash.com/photo-1530549387789-4c1017266635?auto=format&fit=crop&w=1280&q=80"
          alt="" aria-hidden
          className="absolute inset-0 w-full h-full object-cover object-center"
        />
        {/* Overlay degradado */}
        <div className="absolute inset-0" style={{
          background: 'linear-gradient(170deg, rgba(8,32,16,.82) 0%, rgba(28,110,65,.70) 60%, rgba(0, 71, 187,.55) 100%)'
        }} />

        {/* Contenido */}
        <div className="relative z-10 flex flex-col justify-between h-full p-12">
          {/* Badge superior */}
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-2 w-fit">
            <span className="w-2 h-2 rounded-full bg-blue-400 animate-pulse" />
            <span className="text-white/90 text-xs font-semibold tracking-wide">Ñuñoa Deportes</span>
          </div>

          {/* Texto principal */}
          <div>
            <h2 className="text-white font-black text-4xl leading-tight mb-4 drop-shadow">
              Tu deporte,<br />tu comunidad.
            </h2>
            <p className="text-white/70 text-base leading-relaxed max-w-xs mb-10">
              Más de 50 talleres deportivos para todas las edades en Ñuñoa. Inscríbete y sé parte de la comunidad activa.
            </p>

            {/* Stats */}
            <div className="flex gap-8">
              {[{ n: '+50', l: 'Talleres' }, { n: '+8', l: 'Recintos' }, { n: '+1.500', l: 'Inscritos' }].map(s => (
                <div key={s.l}>
                  <p className="text-white font-black text-3xl leading-none">{s.n}</p>
                  <p className="text-white/50 text-sm mt-1">{s.l}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
